import {
	customerPortalUserProjectTable,
	linearIssueTable,
	linearLabelTable,
	linearProjectTable,
	linearWorkflowStateTable,
} from "@mojo/db/schema";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { and, count, eq, inArray, isNull, notInArray } from "drizzle-orm";
import { auth } from "@/lib/auth";
import { db } from "@/lib/db";

export type ProjectWithStats = {
	id: string;
	name: string;
	description: string | null;
	icon: string | null;
	color: string | null;
	state: string;
	progress: number;
	url: string;
	totalIssues: number;
	openIssues: number;
};

export type Label = {
	id: string;
	name: string;
	color: string;
	description: string | null;
};

// Get user's projects with stats
export const getUserProjects = createServerFn({ method: "GET" }).handler(
	async (): Promise<ProjectWithStats[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		// Get user's project IDs
		const userProjects = await db
			.select({ projectId: customerPortalUserProjectTable.projectId })
			.from(customerPortalUserProjectTable)
			.where(eq(customerPortalUserProjectTable.userId, session.user.id));

		const projectIds = userProjects.map((p) => p.projectId);

		if (projectIds.length === 0) {
			return [];
		}

		// Get projects
		const projects = await db
			.select()
			.from(linearProjectTable)
			.where(
				and(
					inArray(linearProjectTable.id, projectIds),
					isNull(linearProjectTable.archivedAt),
				),
			);

		// Get completed workflow states
		const completedStates = await db
			.select({ id: linearWorkflowStateTable.id })
			.from(linearWorkflowStateTable)
			.where(eq(linearWorkflowStateTable.type, "completed"));
		const completedStateIds = completedStates.map((s) => s.id);

		// Get stats for each project
		const projectsWithStats: ProjectWithStats[] = await Promise.all(
			projects.map(async (project) => {
				// Count total issues
				const [{ total: totalIssues }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							eq(linearIssueTable.projectId, project.id),
							isNull(linearIssueTable.archivedAt),
						),
					);

				// Count open issues (not completed)
				const [{ total: openIssues }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							eq(linearIssueTable.projectId, project.id),
							isNull(linearIssueTable.archivedAt),
							completedStateIds.length > 0
								? notInArray(linearIssueTable.stateId, completedStateIds)
								: undefined,
						),
					);

				return {
					id: project.id,
					name: project.name,
					description: project.description,
					icon: project.icon,
					color: project.color,
					state: project.state,
					progress: project.progress,
					url: project.url,
					totalIssues,
					openIssues,
				};
			}),
		);

		return projectsWithStats;
	},
);

// Get available labels for filtering
export const getLabels = createServerFn({ method: "GET" }).handler(
	async (): Promise<Label[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		// Get all non-archived labels
		// In a more sophisticated setup, we might filter by team or org
		const labels = await db
			.select({
				id: linearLabelTable.id,
				name: linearLabelTable.name,
				color: linearLabelTable.color,
				description: linearLabelTable.description,
			})
			.from(linearLabelTable)
			.where(isNull(linearLabelTable.archivedAt));

		return labels;
	},
);

// Get workflow states for filtering
export const getWorkflowStates = createServerFn({ method: "GET" }).handler(
	async (): Promise<
		Array<{ id: string; name: string; type: string; color: string }>
	> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const states = await db
			.select({
				id: linearWorkflowStateTable.id,
				name: linearWorkflowStateTable.name,
				type: linearWorkflowStateTable.type,
				color: linearWorkflowStateTable.color,
			})
			.from(linearWorkflowStateTable)
			.where(isNull(linearWorkflowStateTable.archivedAt));

		return states;
	},
);
